import type { JSX } from 'react';

export interface ChartSeries {
  name: string;
  color: string;
  values: number[];
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 時系列ラインチャート（720x260 viewBox の SVG・複数系列対応）。0 起点で y 軸目盛り 4 段、x 軸ラベルは項目数に応じて間引き、各値にドット表示
 * @graph-connects none
 */
export function SvgLineChart(props: { labels: string[]; series: ChartSeries[] }): JSX.Element {
  const W = 720;
  const H = 260;
  const padL = 50;
  const padR = 16;
  const padT = 16;
  const padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const allVals = props.series.flatMap((s) => s.values);
  const maxV = allVals.length > 0 ? Math.max(...allVals) : 1;
  const minV = 0;
  const span = Math.max(maxV - minV, 1);
  const xStep = props.labels.length > 1 ? innerW / (props.labels.length - 1) : 0;
  const x = (i: number): number => padL + i * xStep;
  const y = (v: number): number => padT + innerH - ((v - minV) / span) * innerH;
  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => minV + (span * i) / yTicks);
  const labelStep = props.labels.length <= 12 ? 1 : Math.ceil(props.labels.length / 8);

  return (
    <>
      <svg viewBox={`0 0 ${String(W)} ${String(H)}`} xmlns="http://www.w3.org/2000/svg">
        <g stroke="#eef0f3" strokeWidth={1}>
          {tickValues.map((t) => (
            <line key={t} x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} />
          ))}
        </g>
        <g fontSize={10} fill="#8a93a1" fontFamily="-apple-system,sans-serif">
          {tickValues.map((t) => (
            <text key={t} x={padL - 6} y={y(t) + 3} textAnchor="end">
              {Math.round(t).toLocaleString()}
            </text>
          ))}
        </g>
        <g fontSize={10} fill="#8a93a1" fontFamily="-apple-system,sans-serif">
          {props.labels.map((l, i) =>
            i % labelStep === 0 || i === props.labels.length - 1 ? (
              <text key={`${l}-${String(i)}`} x={x(i)} y={H - padB + 14} textAnchor="middle">
                {l}
              </text>
            ) : null,
          )}
        </g>
        {props.series.map((s) => {
          const path = s.values
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
            .join(' ');
          return (
            <g key={s.name}>
              <path
                d={path}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {s.values.map((v, i) => (
                <circle
                  key={i}
                  cx={Number(x(i).toFixed(1))}
                  cy={Number(y(v).toFixed(1))}
                  r={3}
                  fill={s.color}
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              ))}
            </g>
          );
        })}
      </svg>
      <div className="chart-legend">
        {props.series.map((s) => (
          <span key={s.name} className="lg-item">
            <span className="lg-line" style={{ background: s.color }}></span>
            {s.name}
          </span>
        ))}
      </div>
    </>
  );
}
